<template lang="html">
  <div id="floorplan-list" class="contained-height">
    <div id="floorplan-list-info"
    class="container-fluid
    col-lg-12 col-md-12 col-12 d-none d-md-block">
      <div class="d-flex align-items-center justify-content-center h-100">
        {{ floorplanListInfo }}
      </div>
    </div>
    <div v-if="filteredFloorplanList.length > 0">
      <transition name="fade" mode="out-in">
        <scrollable-content
        v-if="listDisplayType === 'grid'">
          <template>
            <div class="d-block d-md-none mt-3">
              {{ floorplanListInfo }}
            </div>
            <div v-if="showFavourites && !deviceMd" class="email-blurb mb-5 mt-4 mt-lg-3">
              <div class="email-tagline">Save to your inbox.</div>
              <div class="email-input d-flex mt-3 ml-lg-3">
                  <text-input
                  v-model="email"
                  placeholder="example@email.com"
                  @reset="email = ''"
                  />
                  <span
                    class="btn btn-email ml-lg-3 mt-3 mt-lg-0"
                    :class="{'disabled': sending}"
                    @click="emailFloorplans()"
                  >
                    <i class="fas fa-envelope"></i> Email Floorplans
                  </span>
              </div>
            </div>
            <floorplan-grid :floorplanList="filteredFloorplanList" />
          </template>
        </scrollable-content>
        <scrollable-content
        v-else-if="listDisplayType === 'table' && filteredFloorplanList.length > 0">
          <template>
            <div class="d-block d-md-none mt-3">
              {{ floorplanListInfo }}
            </div>
          <floorplan-table :floorplanList="filteredFloorplanList" />
        </template>
      </scrollable-content>
      </transition>
    </div>
    <div class="pt-4 no-fp d-flex flex-column align-content-stretch" v-else>
      <span v-if="!showFavourites">
        {{ noPlansLabel }}
      </span>
      <div  v-else>
        <span>No favourited floorplans.</span>
        <router-link to="/" class="d-block">Go back to floorplan list.</router-link>
      </div>
    </div>
    <floorplan-list-actions class="d-none d-md-block"/>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import { create } from 'vue-modal-dialogs';
import Email from '@/services/email';
import ScrollableContentComponent from '@/components/ScrollableContent';
import FloorplanGridComponent from '@/components/Floorplan/FloorplanGrid';
import FloorplanTableComponent from '@/components/Floorplan/FloorplanTable';
import TextInput from '@/components/Input/TextInput';
import FloorplanListActionsComponent from './FloorplanListActions';
import fstore from '@/analytics/firestore';

export default {
  data() {
    return {
      email: '',
      sending: false
    };
  },
  computed: {
    ...mapState({
      showFavourites: state => state.floorplans.showFavourites,
      listDisplayType: state => state.floorplans.listDisplayType,
      isTouchscreen: state => state.project.isTouchscreen,
      filters: state => state.floorplans.filters
    }),
    ...mapGetters({
      totalFloorplanCount: 'floorplans/totalFloorplanCount',
      filteredFloorplanList: 'floorplans/filteredFloorplanList',
      deviceMd: 'ui/deviceMd'
    }),
    floorplanListInfo() {
      let infoText =
        `filtered ${this.filteredFloorplanList.length} of ${this.totalFloorplanCount} total floorplans`;
      if (this.filteredFloorplanList.length === this.totalFloorplanCount) {
        infoText =
          `Displaying ${this.totalFloorplanCount} unfiltered floorplans`;
      }
      if (this.showFavourites) {
        infoText =
        `favourited ${this.filteredFloorplanList.length} of ${this.totalFloorplanCount} total floorplans`;
      }
      return infoText;
    },
    noPlansLabel() {
      if (this.filters) {
        if (this.filters.collectionList && this.filters.collectionList.length > 0) {
          const summerWind = this.filters.collectionList.find(x => x.slug === 'summerwind');
          if (summerWind && summerWind.active) {
            if (this.filteredFloorplanList && this.filteredFloorplanList.length === 0) {
              return 'All of our Summer Wind suites are currently leased. Please reset the filter to explore all available suite types';
            }
          }
        }
      }
      return 'No floorplans available based on current filter.';
    }
  },
  methods: {
    ...mapMutations({
      setShowFavourites: 'floorplans/setShowFavourites',
      setSelectedUnitName: 'floorplans/setSelectedUnitName'
    }),
    checkRoute() {
      if (this.$route.name === 'FloorplanFavourites') {
        this.setShowFavourites(true);
      } else {
        this.setShowFavourites(false);
      }
    },
    trackConversion(email) {
      const plans = this.filteredFloorplanList.map(plan => ({
        name: plan.name,
        interior: plan.interior,
        beds: plan.beds,
        den: plan.dens > 0,
        baths: plan.baths,
        exposures: plan.exposure
      }));
      fstore.trackPlansConverted(plans);
      fstore.trackLeads(email, plans);
    },
    async emailFloorplans() {
      const email = this.email;
      if (email) {
        this.$toasted.show('Emailing floorplan...', {
          position: 'top-center',
          duration: 4000
        });
        const emailUrlArr = [];
        const emailUrlName = [];
        this.filteredFloorplanList.forEach((plan) => {
          emailUrlArr.push(plan.assets.pdf.url);
          emailUrlName.push(plan.name);
        });
        const result = await Email.send(email,
          this.filteredFloorplanList);
        this.$toasted.clear();
        if (!this.isTouchscreen) {
          this.trackConversion(email);
        }
        if (result.status === 'success') {
          this.$toasted.show(result.msg, {
            theme: 'success',
            position: 'top-center',
            duration: 4000,
            type: 'success'
          });
        } else {
          this.$toasted.show(result.msg, {
            theme: 'error',
            position: 'top-center',
            duration: 4000,
            type: 'error'
          });
        }
      }
    }
  },
  watch: {
    $route() {
      this.checkRoute();
    }
  },
  created() {
    this.checkRoute();
  },
  mounted() {
    this.setSelectedUnitName('');
  },
  components: {
    'floorplan-list-actions': FloorplanListActionsComponent,
    'floorplan-grid': FloorplanGridComponent,
    'floorplan-table': FloorplanTableComponent,
    'scrollable-content': ScrollableContentComponent,
    'text-input': TextInput
  }
};
</script>

<style lang="scss" scoped>
#floorplan-list-info {
  position: static;
  top: 0;
  text-align: center;
  background: var(--background);
  color: var(--text);
  height: $bottom-bar-height;
}

.no-fp {
  height: calc(100% - #{$app-bar-height} - #{$bottom-bar-height} + 11px);
  // height: 100%;
}

#floorplan-list {
  .btn-email {
    display: inline-block;
    font-family: Radikal-medium, sans-serif;
    color: white;
    border: 2px solid var(--favourite);
    background: var(--favourite);
    &.disabled {
      pointer-events: none;
      opacity: 0.5;
    }

    &:hover {
      //color: hsl(var(--favourite-h), var(--favourite-s), calc(#{var(--favourite-l)} - 8%));
      border-color: hsl(var(--favourite-h), var(--favourite-s), calc(#{var(--favourite-l)} - 8%));
      background: hsl(var(--favourite-h), var(--favourite-s), calc(#{var(--favourite-l)} - 8%));
    }

    @media screen and (max-width: $breakpoint-lg) {
      max-width: 180px;
    }
  }

  .email-blurb {
    text-align: left;

    .email-tagline {
      font-family: Radikal-medium, sans-serif;
      color: var(--branding);
      font-size: 2.4rem;
      line-height: 2rem;

      @media screen and (max-width: $breakpoint-lg) {
        font-size: 2rem;
        line-height: 1.8rem;
      }
    }

    .email-input {
      @media screen and (max-width: $breakpoint-lg) {
        flex-flow: column;
      }
    }

    .text-input {
      width: 100%;
      border-bottom: 2px solid var(--favourite);

      input {
        background: transparent;
        width: 100%;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/FloorplansPage/FloorplanList/FloorplanList.vue